import { useId, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { LoaderCircle, SquareTerminal } from 'lucide-react'
import { Badge as BadgeComponent } from '@/components/ui/badge'
import { Button as ButtonComponent } from '@/components/ui/button'
import { Label as LabelComponent } from '@/components/ui/label'
import { Textarea as TextareaComponent } from '@/components/ui/textarea'
import { HelperDialogLoading } from '@/components/campaigns/HelperDialog'
import { features } from '@/config/features'
import { GET_SUGGESTION_SYSTEM_PROMPT, UPDATE_SUGGESTION_SYSTEM_PROMPT } from '@/graphql/ads/prompt-suggestion-queries'
import { errorFieldOf } from '@/hooks/useLicenseOverviewStep'
import {
    SuggestionPromptKind,
    SuggestionSystemPrompt,
    SuggestionSystemPromptData,
    SuggestionTarget,
    SystemPromptChange,
    systemPromptQueryVariablesOf,
} from '@/hooks/useSuggestionSystemPrompt'
import { ButtonProps } from '@/types'

// Type casts for the UI components from JSX files
const Button = ButtonComponent as React.FC<
    ButtonProps & Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'aria-label'>
>
const Badge = BadgeComponent as React.FC<React.HTMLAttributes<HTMLDivElement> & { variant?: string }>
const Label = LabelComponent as React.FC<React.LabelHTMLAttributes<HTMLLabelElement>>
const Textarea = TextareaComponent as React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>>

export const SYSTEM_PROMPT_MAX_LENGTH = 20000

const SAVE_FAILED = 'We could not save the system prompt. Please try again.'

interface UpdateSuggestionSystemPromptData {
    updateSuggestionSystemPrompt: SuggestionSystemPrompt
}

/** "Edit system prompt" in the suggestions header of an AI helper, next to "Edit overview"; hidden while the feature is off. */
export const EditSystemPromptButton = ({ onClick }: { onClick: () => void }) =>
    features.systemPromptEditing ? (
        <Button type="button" variant="ghost" size="sm" onClick={onClick}>
            <SquareTerminal size={14} />
            Edit system prompt
        </Button>
    ) : null

/** Tells the user a custom system prompt writes the suggestions; hidden while the feature is off. */
export const CustomSystemPromptBadge = () =>
    features.systemPromptEditing ? <Badge variant="campaign">Custom system prompt</Badge> : null

interface SystemPromptFormProps {
    /** The saved license the prompt is saved on; without one the text is only handed back to the helper */
    campaignId: string | null
    kind: SuggestionPromptKind
    initialPrompt: string
    custom: boolean
    onCancel: () => void
    onApplied: (change: SystemPromptChange) => void
}

/**
 * The textarea and actions of the system prompt editor. A saved license's prompt is saved (or reset to the built-in
 * one) on the server before the helper regenerates; a license that is not saved yet hands the text back instead.
 */
const SystemPromptForm = ({ campaignId, kind, initialPrompt, custom, onCancel, onApplied }: SystemPromptFormProps) => {
    const id = useId()
    const [text, setText] = useState(initialPrompt)
    const [error, setError] = useState<string | null>(null)
    const [save, { loading: saving }] = useMutation<UpdateSuggestionSystemPromptData>(UPDATE_SUGGESTION_SYSTEM_PROMPT)
    const saved = Boolean(campaignId)
    const ids = { textarea: `${id}-text`, hint: `${id}-hint`, count: `${id}-count`, error: `${id}-error` }

    const persist = async (prompt: string | null) => {
        setError(null)
        try {
            const { data } = await save({ variables: { campaignId, kind, prompt } })
            onApplied({ override: null, custom: data?.updateSuggestionSystemPrompt?.custom ?? prompt !== null })
        } catch (saveError) {
            const field = errorFieldOf(saveError)
            setError(
                field === 'prompt' || field === 'systemPrompt'
                    ? (saveError as Error).message || SAVE_FAILED
                    : SAVE_FAILED,
            )
        }
    }

    const handleApply = () => {
        const prompt = text.trim()
        if (saved) persist(prompt)
        else onApplied({ override: prompt, custom: true })
    }

    const handleReset = () => {
        if (saved) persist(null)
        else onApplied({ override: null, custom: false })
    }

    return (
        <div className="tw:flex tw:flex-col tw:gap-2">
            <div className="tw:flex tw:items-center tw:gap-2">
                <Label htmlFor={ids.textarea}>System prompt</Label>
                {custom && <Badge variant="campaign">Custom</Badge>}
            </div>
            <p id={ids.hint} className="tw:text-xs tw:text-gray-400 tw:m-0">
                The instructions the AI follows to write these suggestions. The business details, overview and your
                draft are sent separately.
            </p>
            {!saved && (
                <p className="tw:text-xs tw:text-gray-400 tw:m-0">
                    Used until you close this window; it is not saved because the license is not created yet.
                </p>
            )}
            <Textarea
                id={ids.textarea}
                rows={16}
                maxLength={SYSTEM_PROMPT_MAX_LENGTH}
                value={text}
                onChange={(event) => setText(event.target.value)}
                disabled={saving}
                spellCheck={false}
                aria-invalid={Boolean(error)}
                aria-describedby={[ids.hint, ids.count, error && ids.error].filter(Boolean).join(' ')}
                className="tw:h-auto tw:min-h-48 tw:pr-3 tw:field-sizing-fixed tw:font-mono tw:!text-xs tw:leading-relaxed"
                style={{ resize: 'vertical' }}
            />
            <span id={ids.count} className="tw:text-xs tw:text-gray-400 tw:self-end">
                {text.length}/{SYSTEM_PROMPT_MAX_LENGTH}
            </span>
            {error && (
                <p id={ids.error} role="alert" className="tw:text-sm tw:text-red-400 tw:m-0">
                    {error}
                </p>
            )}
            <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-end tw:gap-2">
                {custom && (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="tw:mr-auto"
                        onClick={handleReset}
                        disabled={saving}
                    >
                        Reset to default
                    </Button>
                )}
                <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
                    Cancel
                </Button>
                <Button type="button" size="sm" onClick={handleApply} disabled={saving || !text.trim()}>
                    {saving && <LoaderCircle size={16} className="tw:animate-spin" />}
                    {saving ? 'Saving…' : saved ? 'Save and regenerate' : 'Use and regenerate'}
                </Button>
            </div>
        </div>
    )
}

interface SystemPromptEditorProps {
    kind: SuggestionPromptKind
    /** The license the last suggestions were for, as the suggestion request named it */
    target: SuggestionTarget
    /** The text the suggestions improve, as the suggestion request sent it; left out when they improve none */
    draft?: string | null
    /** The prompt the dialog uses instead of the built-in one, for a license that is not saved yet */
    override?: string | null
    onCancel: () => void
    /** Called once the prompt is saved, reset or (for a license not saved yet) chosen; the helper then regenerates */
    onApplied: (change: SystemPromptChange) => void
}

/**
 * The "Edit system prompt" view of an AI helper's dialog: loads the system prompt the next suggestions of this kind
 * use (a saved license's custom prompt, else the built-in one) and lets the user edit it. A saved license saves it,
 * or resets it to the built-in prompt, before the helper regenerates; a license that is not saved yet cannot save it,
 * so the edited text is handed back for the helper to send with its requests until the dialog closes.
 */
const SystemPromptEditor = ({ kind, target, draft, override = null, onCancel, onApplied }: SystemPromptEditorProps) => {
    const { data, loading, error } = useQuery<SuggestionSystemPromptData>(GET_SUGGESTION_SYSTEM_PROMPT, {
        variables: systemPromptQueryVariablesOf(target, kind, draft),
        fetchPolicy: 'network-only',
    })

    if (loading) return <HelperDialogLoading label="Loading the system prompt…" />

    if (error || !data?.suggestionSystemPrompt) {
        return (
            <div className="tw:flex tw:flex-col tw:gap-3">
                <p role="alert" className="tw:text-sm tw:text-red-400 tw:m-0">
                    We could not load the system prompt. Please try again.
                </p>
                <div className="tw:flex tw:justify-end">
                    <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
                        Back to suggestions
                    </Button>
                </div>
            </div>
        )
    }

    const { prompt, custom } = data.suggestionSystemPrompt
    return (
        <SystemPromptForm
            campaignId={target.campaignId ?? null}
            kind={kind}
            initialPrompt={override ?? prompt}
            custom={override !== null || custom}
            onCancel={onCancel}
            onApplied={onApplied}
        />
    )
}

export default SystemPromptEditor
